/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React from 'react'
import clsx from 'clsx'
import {useTranslation} from 'react-i18next'
import {ArrowUturnLeftIcon, PencilIcon, TrashIcon,} from '@heroicons/react/24/outline'
import Dates from 'utils/Dates'
import {DATE_FORMATS} from 'constants/DateFormats'
import type {OfferNote} from 'types/entities/OfferNote'

interface OfferBackOfficeNoteItemProps {
    note: OfferNote
    isReply?: boolean
    currentUser: any
    repliesMap: Map<number, OfferNote[]>
    getInitials: (name: string) => string
    onReply: (note: OfferNote) => void
    onEdit: (note: OfferNote) => void
    onDelete: (id: number) => void
    renderNote: (note: OfferNote, isReply?: boolean) => React.ReactNode
    disabled?: boolean
}

const OfferBackOfficeNoteItem: React.FC<OfferBackOfficeNoteItemProps> = ({
    note,
    isReply = false,
    currentUser,
    repliesMap,
    getInitials,
    onReply,
    onEdit,
    onDelete,
    renderNote,
    disabled,
}) => {
    const { t } = useTranslation()

    const isCreator =
        currentUser?.uid === note.creator_id ||
        currentUser?.uid === note.creator?.id
    const creatorName =
        note.creator?.name || (isCreator ? t('You') : t('Unknown'))

    const noteReplies = repliesMap.get(note.id) || []
    const noteContent = note.note || ''
    const hasHtml = /<\/?[a-z][\s\S]*>/i.test(noteContent)

    return (
        <div
            className={clsx(
                'OfferBackOfficeNoteItem flex flex-col',
                isReply && 'ml-4 mt-2'
            )}
        >
            <div
                className={clsx(
                    'group flex gap-2 py-1.5 px-2 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors border border-transparent hover:border-gray-100 dark:hover:border-gray-800 rounded-lg relative'
                )}
            >
                <div className="shrink-0">
                    <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-[10px] font-bold text-gray-600 dark:text-gray-300">
                        {getInitials(creatorName)}
                    </div>
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                        <div className="flex items-start gap-x-2">
                            <span className="text-xs font-bold text-gray-900 dark:text-gray-100">
                                {creatorName}
                            </span>
                            <span className="text-[10px] text-gray-700">
                                {Dates.dayjs(note.created_at).format(
                                    DATE_FORMATS.DATE_TIME
                                )}
                            </span>
                        </div>

                        {!disabled && (
                        <div className="flex items-center gap-1 opacity-50 group-hover:opacity-100 transition-opacity">
                            <button
                                onClick={() => onReply(note)}
                                className="p-1 text-gray-600 hover:text-blue-500 rounded"
                                title={t('Reply') as string}
                            >
                                <ArrowUturnLeftIcon className="size-3" />
                            </button>
                            {isCreator && (
                                <>
                                    <button
                                        onClick={() => onEdit(note)}
                                        className="p-1 text-gray-400 hover:text-blue-500 rounded transition-colors"
                                        title={t('Edit')}
                                    >
                                        <PencilIcon className="w-3" />
                                    </button>
                                    <button
                                        onClick={() => onDelete(note.id)}
                                        className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors"
                                        title={t('Delete')}
                                    >
                                        <TrashIcon className="w-3" />
                                    </button>
                                </>
                            )}
                        </div>
                        )}
                    </div>
                    {hasHtml ? (
                        <div
                            className="UiRichTextEditorContent text-xs text-gray-800 dark:text-gray-300 break-words leading-relaxed [&_p]:my-1 [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4 [&_li]:my-0.5 [&_strong]:font-semibold [&_em]:italic"
                            dangerouslySetInnerHTML={{ __html: noteContent }}
                        />
                    ) : (
                        <div className="text-xs text-gray-800 dark:text-gray-300 break-words leading-relaxed whitespace-pre-wrap">
                            {noteContent}
                        </div>
                    )}
                </div>
            </div>
            {noteReplies.length > 0 && (
                <div className="flex flex-col border-l-2 border-gray-100 dark:border-gray-800 ml-4 pl-0">
                    {noteReplies.map((reply) => renderNote(reply, true))}
                </div>
            )}
        </div>
    )
}

export default OfferBackOfficeNoteItem
